import { fromB64, open, randomBytes, seal, toB64 } from './vault.ts';

/**
 * Entsperren per Passkey (docs/KONZEPT.md, Abschnitt 18).
 *
 * WebAuthn kann über die PRF-Erweiterung aus einem Passkey ein stabiles Geheimnis
 * ableiten. Damit wird der Vault Key verpackt und liegt verschlüsselt im Gerät;
 * Face ID oder Fingerabdruck packen ihn wieder aus.
 *
 * **Der Passkey authentifiziert nicht gegenüber dem Server.** Er ist ausschließlich
 * ein Schlüsselableiter auf dem Gerät — die Sitzung läuft weiter über das gespeicherte
 * Token. Deshalb wird die Signatur auch nirgends geprüft: Es gibt nichts zu prüfen,
 * denn ohne das richtige PRF-Geheimnis lässt sich der Vault Key schlicht nicht öffnen.
 *
 * Die Passphrase bleibt der Wurzelschlüssel und der Weg auf jedes neue Gerät.
 */

export type PasskeyRecord = {
  /** Rohdaten der Credential-ID, Base64. */
  credentialId: string;
  /** Zufälliger Wert, mit dem das Geheimnis abgeleitet wird — je Gerät einer. */
  prfSalt: string;
  /** Vault Key, verschlüsselt mit dem abgeleiteten Geheimnis. */
  wrapped: string;
};

type PrfResults = { prf?: { enabled?: boolean; results?: { first?: ArrayBuffer } } };

/** Die PRF-Erweiterung fehlt in den DOM-Typen; sie ist neuer als die Definitionen. */
type WithPrf = { prf: { eval: { first: BufferSource } } };

export class PasskeyUnsupported extends Error {
  constructor() {
    super('Dieses Gerät unterstützt die PRF-Erweiterung nicht.');
  }
}

export class PasskeyCancelled extends Error {
  constructor() {
    super('Passkey-Abfrage abgebrochen.');
  }
}

/**
 * WebAuthn erwartet echte ArrayBuffer. Die Typen von `Uint8Array` lassen inzwischen
 * auch `SharedArrayBuffer` zu und passen deshalb nicht mehr direkt.
 */
function buf(bytes: Uint8Array): ArrayBuffer {
  return bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
}

export function passkeySupported(): boolean {
  // WebAuthn verlangt einen sicheren Kontext und eine echte Domäne — über eine
  // IP-Adresse aufgerufen lehnt der Browser mit "invalid domain" ab.
  const host = location.hostname;
  const domainOk = host === 'localhost' || !/^[\d.]+$/.test(host);
  return (
    typeof PublicKeyCredential !== 'undefined' &&
    !!navigator.credentials?.create &&
    window.isSecureContext &&
    domainOk
  );
}

function prfSecret(credential: PublicKeyCredential): Uint8Array | null {
  const results = credential.getClientExtensionResults() as PrfResults;
  const first = results.prf?.results?.first;
  // Ein zu kurzes Geheimnis stillschweigend zu strecken wäre schlechter als abbrechen.
  if (!first || first.byteLength < 32) return null;
  return new Uint8Array(first).slice(0, 32);
}

/**
 * Holt das abgeleitete Geheimnis. Bei der Anmeldung liefern nicht alle Browser die
 * PRF-Auswertung schon mit `create()`, deshalb wird immer zusätzlich ein `get()`
 * gemacht — das kostet eine zweite Gesichtserkennung, aber nur einmalig beim Einrichten.
 */
async function readSecret(credentialId: Uint8Array, salt: Uint8Array): Promise<Uint8Array> {
  let assertion: PublicKeyCredential | null;
  try {
    assertion = (await navigator.credentials.get({
      publicKey: {
        challenge: buf(randomBytes(32)),
        allowCredentials: [{ type: 'public-key', id: buf(credentialId) }],
        userVerification: 'required',
        // Ohne erreichbaren Authentifikator wartet der Browser bis zum Limit, statt
        // abzubrechen. Eine Minute schweigende Oberfläche ist zu lang.
        timeout: 30_000,
        extensions: { prf: { eval: { first: buf(salt) } } } as unknown as WithPrf,
      },
    })) as PublicKeyCredential | null;
  } catch {
    throw new PasskeyCancelled();
  }
  if (!assertion) throw new PasskeyCancelled();

  const secret = prfSecret(assertion);
  if (!secret) throw new PasskeyUnsupported();
  return secret;
}

/**
 * Legt einen Passkey an und verpackt den Vault Key damit. Muss bei entsperrtem Vault
 * aufgerufen werden — der Schlüssel kommt aus dem Arbeitsspeicher.
 */
export async function registerPasskey(
  email: string,
  vaultKey: Uint8Array,
): Promise<PasskeyRecord> {
  if (!passkeySupported()) throw new PasskeyUnsupported();

  const salt = randomBytes(32);
  let credential: PublicKeyCredential | null;
  try {
    credential = (await navigator.credentials.create({
      publicKey: {
        challenge: buf(randomBytes(32)),
        // Ohne `id` nimmt der Browser die Domäne des Ursprungs. Das ist nicht nur
        // kürzer, sondern auch richtiger: eine selbst gesetzte, nicht passende
        // Domäne lehnt WebAuthn ab.
        rp: { name: 'Athene' },
        user: { id: buf(randomBytes(16)), name: email || 'athene', displayName: email || 'Athene' },
        pubKeyCredParams: [
          { type: 'public-key', alg: -7 },
          { type: 'public-key', alg: -257 },
        ],
        authenticatorSelection: {
          residentKey: 'required',
          userVerification: 'required',
        },
        timeout: 60_000,
        extensions: { prf: { eval: { first: buf(salt) } } } as unknown as WithPrf,
      },
    })) as PublicKeyCredential | null;
  } catch {
    throw new PasskeyCancelled();
  }
  if (!credential) throw new PasskeyCancelled();

  // `enabled: false` heißt: Der Authentifikator kann kein PRF. Dann hat der Passkey
  // hier keinen Zweck und wird gar nicht erst vermerkt.
  const extensions = credential.getClientExtensionResults() as PrfResults;
  if (extensions.prf?.enabled === false) throw new PasskeyUnsupported();

  const credentialId = new Uint8Array(credential.rawId);
  const secret = await readSecret(credentialId, salt);
  const record: PasskeyRecord = {
    credentialId: toB64(credentialId),
    prfSalt: toB64(salt),
    wrapped: seal(vaultKey, secret),
  };
  secret.fill(0);
  return record;
}

/** Packt den Vault Key mit dem Passkey wieder aus. */
export async function unlockWithPasskey(record: PasskeyRecord): Promise<Uint8Array> {
  if (!passkeySupported()) throw new PasskeyUnsupported();

  const secret = await readSecret(fromB64(record.credentialId), fromB64(record.prfSalt));
  try {
    return open(record.wrapped, secret);
  } finally {
    secret.fill(0);
  }
}
